import {
    WasteReturnFixedCellKeys,
    WasteReturnOverflowStrategies,
    WasteReturnRegFieldKeys,
    WasteReturnRowModes,
    WasteReturnTableColumnKeys,
    WasteReturnTableKeys,
    WasteReturnValueFormatters,
    WasteReturnPairedColumnInputKeys,
    WasteReturnPairedColumnRoles,
} from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";

export const EA_IMPORT_TABLE_MAX_ROWS = 10_000;
export const EA_IMPORT_TABLE_ROW_MODE = WasteReturnRowModes.FixedRange;
export const EA_IMPORT_TABLE_OVERFLOW_STRATEGY = WasteReturnOverflowStrategies.Fail;

export type EaWasteReturnFieldKind = "singleCell" | "tableColumn" | "pairedColumnPart";

export type EaWasteReturnFieldSection = "siteInformation" | "technicalCompetence" | "wasteReceived" | "wasteRemoved";

export interface IEaWasteReturnFieldDefinition {
    /** Stable key used in flat rows and adapter wiring */
    fieldKey: string;
    label: string;
    section: EaWasteReturnFieldSection;
    kind: EaWasteReturnFieldKind;
    /** Maps to fixedCells key or tables.columns key */
    mappingKey: string;
    tableKey?: typeof WasteReturnTableKeys.WasteReceived | typeof WasteReturnTableKeys.WasteRemoved;
    defaultSheet: string;
    defaultAddress: string;
    valueFormatter: string;
    regFieldKey?: string | null;
    maxLength?: number | null;
    pairKey?: string;
    pairRole?: typeof WasteReturnPairedColumnRoles.Names | typeof WasteReturnPairedColumnRoles.Dobs;
}

export const EA_WASTE_RETURN_FIELD_SECTION_LABELS: Record<EaWasteReturnFieldSection, string> = {
    siteInformation: "Site Information Inputs",
    technicalCompetence: "Technical Competence",
    wasteReceived: "Waste Received Inputs",
    wasteRemoved: "Waste Removed Inputs",
};

export const EA_WASTE_RETURN_IMPORT_FIELD_DEFINITIONS: IEaWasteReturnFieldDefinition[] = [
    {
        fieldKey: "reportingPeriod",
        label: "Period name",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.ReportingPeriod,
        defaultSheet: "Site_Information",
        defaultAddress: "B19",
        valueFormatter: WasteReturnValueFormatters.Raw,
    },
    {
        fieldKey: "reportingYear",
        label: "Year",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.ReportingYear,
        defaultSheet: "Site_Information",
        defaultAddress: "D19",
        valueFormatter: WasteReturnValueFormatters.Raw,
    },
    {
        fieldKey: "siteOperatorName",
        label: "Operator name",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.SiteOperatorName,
        defaultSheet: "Site_Information",
        defaultAddress: "B24",
        valueFormatter: WasteReturnValueFormatters.Raw,
    },
    {
        fieldKey: "permitNumber",
        label: "Permit number",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.PermitNumber,
        defaultSheet: "Site_Information",
        defaultAddress: "B26",
        valueFormatter: WasteReturnValueFormatters.Raw,
        maxLength: 5,
    },
    {
        fieldKey: "siteName",
        label: "Site name",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.SiteName,
        defaultSheet: "Site_Information",
        defaultAddress: "B28",
        valueFormatter: WasteReturnValueFormatters.Raw,
    },
    {
        fieldKey: "siteAddress",
        label: "Site Address",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.SiteAddress,
        defaultSheet: "Site_Information",
        defaultAddress: "B30:E33",
        valueFormatter: WasteReturnValueFormatters.Raw,
    },
    {
        fieldKey: "sitePostcode",
        label: "Post Code",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.SitePostcode,
        defaultSheet: "Site_Information",
        defaultAddress: "C34",
        valueFormatter: WasteReturnValueFormatters.Raw,
    },
    {
        fieldKey: "weighbridgeUsed",
        label: "Weighbridge used",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.WeighbridgeUsed,
        defaultSheet: "Site_Information",
        defaultAddress: "D40",
        valueFormatter: WasteReturnValueFormatters.YesNo,
    },
    {
        fieldKey: "percentageWeighed",
        label: "Percentage weighed",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.PercentageWeighed,
        defaultSheet: "Site_Information",
        defaultAddress: "D41",
        valueFormatter: WasteReturnValueFormatters.Decimal,
    },
    {
        fieldKey: "formComplete",
        label: "Form Complete",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.FormComplete,
        defaultSheet: "Site_Information",
        defaultAddress: "H39",
        valueFormatter: WasteReturnValueFormatters.YesNo,
    },
    {
        fieldKey: "signatoryName",
        label: "Name",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.SignatoryName,
        defaultSheet: "Site_Information",
        defaultAddress: "H40",
        valueFormatter: WasteReturnValueFormatters.Raw,
    },
    {
        fieldKey: "signatoryPosition",
        label: "Position",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.SignatoryPosition,
        defaultSheet: "Site_Information",
        defaultAddress: "H41",
        valueFormatter: WasteReturnValueFormatters.Raw,
    },
    {
        fieldKey: "signatoryPhone",
        label: "Phone",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.SignatoryPhone,
        defaultSheet: "Site_Information",
        defaultAddress: "H42",
        valueFormatter: WasteReturnValueFormatters.Raw,
    },
    {
        fieldKey: "nilReturn",
        label: "Nil Return",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.NilReturn,
        defaultSheet: "Site_Information",
        defaultAddress: "K40",
        valueFormatter: WasteReturnValueFormatters.YesNo,
    },
    {
        fieldKey: "formCompletionDate",
        label: "Date",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.FormCompletionDate,
        defaultSheet: "Site_Information",
        defaultAddress: "K42",
        valueFormatter: WasteReturnValueFormatters.Date,
    },
    {
        fieldKey: "facilityOperation",
        label: "Facility operation",
        section: "siteInformation",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.FacilityOperation,
        defaultSheet: "Site_Information",
        defaultAddress: "B37",
        valueFormatter: WasteReturnValueFormatters.CodeDescription,
        regFieldKey: WasteReturnRegFieldKeys.FacilityOperation,
    },
    {
        fieldKey: "competenceScheme",
        label: "Competence scheme",
        section: "technicalCompetence",
        kind: "singleCell",
        mappingKey: WasteReturnFixedCellKeys.CompetenceScheme,
        defaultSheet: "Technical Competence",
        defaultAddress: "I2",
        valueFormatter: WasteReturnValueFormatters.Code,
        regFieldKey: WasteReturnRegFieldKeys.TechnicalCompetence,
    },
    {
        fieldKey: "tcManagerNames",
        label: "Technically competent manager names",
        section: "technicalCompetence",
        kind: "pairedColumnPart",
        mappingKey: WasteReturnPairedColumnInputKeys.TcManagers,
        pairKey: WasteReturnPairedColumnInputKeys.TcManagers,
        pairRole: WasteReturnPairedColumnRoles.Names,
        defaultSheet: "Technical Competence",
        defaultAddress: "H11:H16",
        valueFormatter: WasteReturnValueFormatters.Raw,
    },
    {
        fieldKey: "tcManagerDobs",
        label: "Technically competent manager DOBs",
        section: "technicalCompetence",
        kind: "pairedColumnPart",
        mappingKey: WasteReturnPairedColumnInputKeys.TcManagers,
        pairKey: WasteReturnPairedColumnInputKeys.TcManagers,
        pairRole: WasteReturnPairedColumnRoles.Dobs,
        defaultSheet: "Technical Competence",
        defaultAddress: "I11:I16",
        valueFormatter: WasteReturnValueFormatters.Date,
    },
    {
        fieldKey: "origin",
        label: "Origin",
        section: "wasteReceived",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.Origin,
        tableKey: WasteReturnTableKeys.WasteReceived,
        defaultSheet: "Waste_Received",
        defaultAddress: "B8",
        valueFormatter: WasteReturnValueFormatters.Description,
        regFieldKey: WasteReturnRegFieldKeys.Geography,
    },
    {
        fieldKey: "ewcReceived",
        label: "EWC code",
        section: "wasteReceived",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.EwcCode,
        tableKey: WasteReturnTableKeys.WasteReceived,
        defaultSheet: "Waste_Received",
        defaultAddress: "C8",
        valueFormatter: WasteReturnValueFormatters.CodeDescription,
        regFieldKey: WasteReturnRegFieldKeys.Ewc,
    },
    {
        fieldKey: "drReceived",
        label: "D/R code (received)",
        section: "wasteReceived",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.DisposalRecoveryCode,
        tableKey: WasteReturnTableKeys.WasteReceived,
        defaultSheet: "Waste_Received",
        defaultAddress: "D8",
        valueFormatter: WasteReturnValueFormatters.Code,
        regFieldKey: WasteReturnRegFieldKeys.EaDrReceived,
    },
    {
        fieldKey: "municipalReceived",
        label: "Municipal flag",
        section: "wasteReceived",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.MunicipalFlag,
        tableKey: WasteReturnTableKeys.WasteReceived,
        defaultSheet: "Waste_Received",
        defaultAddress: "E8",
        valueFormatter: WasteReturnValueFormatters.YesNo,
        regFieldKey: WasteReturnRegFieldKeys.Municipal,
    },
    {
        fieldKey: "degradable",
        label: "Degradable flag",
        section: "wasteReceived",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.DegradableFlag,
        tableKey: WasteReturnTableKeys.WasteReceived,
        defaultSheet: "Waste_Received",
        defaultAddress: "F8",
        valueFormatter: WasteReturnValueFormatters.YesNo,
        regFieldKey: WasteReturnRegFieldKeys.Degradable,
    },
    {
        fieldKey: "physicalStateReceived",
        label: "Physical state",
        section: "wasteReceived",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.PhysicalState,
        tableKey: WasteReturnTableKeys.WasteReceived,
        defaultSheet: "Waste_Received",
        defaultAddress: "G8",
        valueFormatter: WasteReturnValueFormatters.Description,
        regFieldKey: WasteReturnRegFieldKeys.PhysicalState,
    },
    {
        fieldKey: "sourceActivity",
        label: "Source activity",
        section: "wasteReceived",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.SourceActivity,
        tableKey: WasteReturnTableKeys.WasteReceived,
        defaultSheet: "Waste_Received",
        defaultAddress: "H8",
        valueFormatter: WasteReturnValueFormatters.Description,
        regFieldKey: WasteReturnRegFieldKeys.SourceActivity,
    },
    {
        fieldKey: "amountReceived",
        label: "Amount (tonnes)",
        section: "wasteReceived",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.AmountTonnes,
        tableKey: WasteReturnTableKeys.WasteReceived,
        defaultSheet: "Waste_Received",
        defaultAddress: "I8",
        valueFormatter: WasteReturnValueFormatters.Decimal,
    },
    {
        fieldKey: "preTreatment",
        label: "Pre-treatment",
        section: "wasteReceived",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.PreTreatment,
        tableKey: WasteReturnTableKeys.WasteReceived,
        defaultSheet: "Waste_Received",
        defaultAddress: "J8",
        valueFormatter: WasteReturnValueFormatters.Description,
        regFieldKey: WasteReturnRegFieldKeys.PreTreatment,
    },
    {
        fieldKey: "destination",
        label: "Destination",
        section: "wasteRemoved",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.Destination,
        tableKey: WasteReturnTableKeys.WasteRemoved,
        defaultSheet: "Waste_Removed",
        defaultAddress: "B8",
        valueFormatter: WasteReturnValueFormatters.Description,
        regFieldKey: WasteReturnRegFieldKeys.Geography,
    },
    {
        fieldKey: "ewcRemoved",
        label: "EWC code",
        section: "wasteRemoved",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.EwcCode,
        tableKey: WasteReturnTableKeys.WasteRemoved,
        defaultSheet: "Waste_Removed",
        defaultAddress: "C8",
        valueFormatter: WasteReturnValueFormatters.CodeDescription,
        regFieldKey: WasteReturnRegFieldKeys.Ewc,
    },
    {
        fieldKey: "municipalRemoved",
        label: "Municipal flag",
        section: "wasteRemoved",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.MunicipalFlag,
        tableKey: WasteReturnTableKeys.WasteRemoved,
        defaultSheet: "Waste_Removed",
        defaultAddress: "D8",
        valueFormatter: WasteReturnValueFormatters.YesNo,
        regFieldKey: WasteReturnRegFieldKeys.Municipal,
    },
    {
        fieldKey: "physicalStateRemoved",
        label: "Physical state",
        section: "wasteRemoved",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.PhysicalState,
        tableKey: WasteReturnTableKeys.WasteRemoved,
        defaultSheet: "Waste_Removed",
        defaultAddress: "E8",
        valueFormatter: WasteReturnValueFormatters.Description,
        regFieldKey: WasteReturnRegFieldKeys.PhysicalState,
    },
    {
        fieldKey: "drRemoved",
        label: "D/R code (removed)",
        section: "wasteRemoved",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.DisposalRecoveryCode,
        tableKey: WasteReturnTableKeys.WasteRemoved,
        defaultSheet: "Waste_Removed",
        defaultAddress: "F8",
        valueFormatter: WasteReturnValueFormatters.Code,
        regFieldKey: WasteReturnRegFieldKeys.EaDrRemoved,
    },
    {
        fieldKey: "amountRemoved",
        label: "Amount (tonnes)",
        section: "wasteRemoved",
        kind: "tableColumn",
        mappingKey: WasteReturnTableColumnKeys.AmountTonnes,
        tableKey: WasteReturnTableKeys.WasteRemoved,
        defaultSheet: "Waste_Removed",
        defaultAddress: "G8",
        valueFormatter: WasteReturnValueFormatters.Decimal,
    },
];

export function getFieldsBySection(section: EaWasteReturnFieldSection): IEaWasteReturnFieldDefinition[] {
    return EA_WASTE_RETURN_IMPORT_FIELD_DEFINITIONS.filter((field) => field.section === section);
}
